import React, { FC, useCallback, useEffect, useState } from 'react'
import { NavLink, useHistory } from 'react-router-dom'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Box, Checkbox, CheckboxProps, FormControlLabel, Grid, TextField, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { withStyles, makeStyles } from '@mui/styles'

import { useDispatch, useSelector } from 'react-redux'
import { errorMessage } from '../../../constants/errorMessages'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { isUserLoggingInSelector } from '../../../store/selectors'
import { loginRequest, redirectTo } from '../../../store/actions'
import { redirectPathSelector } from '../../../store/selectors/app'

type Inputs = {
  username: string
  password: string
  promp?: string
}

const useStyle = makeStyles((theme: Theme) => ({
  container: {
    marginTop: 176,
    paddingTop: 129,
    textAlign: 'center',
    borderTop: '1px solid rgba(0,0,0,0.24)',
  },
  section: {
    maxWidth: 496,
  },
  title: {
    fontFamily: 'Montserrat',
    textAlign: 'left',
    fontSize: 40,
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  form: {
    marginTop: 64,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  input: {
    fontSize: 16,
    marginBottom: 16,
    padding: '8px 0',
    color: theme.palette.success.main,
    fontFamily: 'Montserrat',
  },
  button: {
    fontFamily: 'Montserrat',
    borderRadius: 28,
    minHeight: 56,
    background: theme.palette.success.main,
    marginTop: 32,
  },
  forgetPass: {
    color: '#584DE2',
    fontWeight: 'bold',
    fontSize: 16,
    cursor: 'pointer',
    textAlign: 'left',
  },
  link: {
    textDecoration: 'none',
  },
  errorField: {
    color: 'red',
  },
}))

const GreenCheckbox = withStyles({
  root: {
    color: '#3E3E3E',
    '&$checked': {
      color: '#51E5A5',
    },
  },
  checked: {},
})((props: CheckboxProps) => <Checkbox color="default" {...props} />)

export const LeftPanel: FC = () => {
  const classes = useStyle()
  const history = useHistory()
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<Inputs>()
  // eslint-disable-next-line
  const [checked, setChecked] = useState<boolean>(false)
  const dispatch = useDispatch()
  const isUserLoggingIn = useSelector(isUserLoggingInSelector)
  const redirectPath = useSelector(redirectPathSelector)

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    (data: Inputs) => {
      dispatch(loginRequest(data))
    },
    [dispatch],
  )

  useEffect(() => {
    if (redirectPath) {
      history.push(redirectPath)
      dispatch(redirectTo(null))
    }
  }, [redirectPath, history, dispatch])

  const handleChange = () => {
    setChecked(!checked)
  }

  return (
    <Grid className={classes.container}>
      <Grid className={classes.section}>
        <Typography variant="h2" className={classes.title}>
          Login
        </Typography>

        <form className="root" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Grid container className={classes.form}>
            <TextField
              className={classes.input}
              variant="standard"
              required
              id="standard-basic-name"
              label="User Name"
              {...register('username', { required: true })}
            />
            {errors.username && (
              <Typography variant="body1" gutterBottom className={classes.errorField}>
                {errorMessage.EMPTY_FIELD}
              </Typography>
            )}

            <TextField
              className={classes.input}
              variant="standard"
              required
              id="standard-basic-password"
              label="Password"
              type="password"
              {...register('password', { required: true })}
            />
            {errors.password && (
              <Typography variant="body1" gutterBottom className={classes.errorField}>
                {errorMessage.EMPTY_FIELD}
              </Typography>
            )}

            <FormControlLabel
              control={<GreenCheckbox checked={checked} onChange={handleChange} name="checked" />}
              label="Keep me signed in"
            />

            <GreenButton type="submit" width="100%" mt={32} text={'Login'} disabled={isUserLoggingIn} noHistory />

            <Box pt={4} pb={16}>
              <NavLink to="/reset-password" className={classes.link}>
                <Typography className={classes.forgetPass}>*** Forgot your password?</Typography>
              </NavLink>
            </Box>
          </Grid>
        </form>
      </Grid>
    </Grid>
  )
}
